/**
 * A real screenshot of the app, framed like the window it is.
 *
 * The captures already contain StreamNook's own title bar, so nothing draws
 * chrome around them the way the old abstract mockups did; the frame here is
 * just the page's glass surface with the image clipped to its radius.
 *
 * Each shot ships at several widths (`name-<width>.webp`, built by the capture
 * harness at 0.6x, 1.5x and 2.5x its widest slot). The smallest candidate a
 * desktop can pick is therefore still half again wider than the box it goes in,
 * which matters here in a way it would not for a photograph: the image is small
 * text that has already been downscaled from the capture, and rendering it 1:1
 * left it mushy. `sizes` has to describe the real slot for that to work.
 *
 * `width` and `height` are the SOURCE capture's pixel dimensions. Only their
 * ratio is used (the image is `w-full h-auto`), and it reserves the right box
 * so nothing below shifts while the picture loads.
 */
export function Shot({ className = '', ...img }: ShotProps & { className?: string }) {
  return (
    <div className={`liquid-glass-panel overflow-hidden ${className}`}>
      <ShotImage {...img} className="block h-auto w-full" />
    </div>
  );
}

interface ShotProps {
  /** Base file name under `public/shots`, without the width suffix. */
  name: string;
  alt: string;
  /** The widths built for this shot, ascending. */
  widths: number[];
  /** The source capture's own dimensions, for the aspect ratio. */
  width: number;
  height: number;
  /** What the browser should expect the rendered width to be. */
  sizes: string;
  /** Set on the hero shot only: it is the page's largest contentful paint. */
  priority?: boolean;
}

/** The bare picture, for callers that draw their own frame around it. */
export function ShotImage({
  name,
  alt,
  widths,
  width,
  height,
  sizes,
  priority = false,
  className = '',
}: ShotProps & { className?: string }) {
  return (
    <img
      src={`/shots/${name}-${widths[widths.length - 1]}.webp`}
      srcSet={widths.map(w => `/shots/${name}-${w}.webp ${w}w`).join(', ')}
      sizes={sizes}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      className={className}
    />
  );
}
